import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, Check, Heart } from "lucide-react";

const AMOUNTS = [25, 50, 100, 250, 500, 1000];

export default function Donate() {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState("one-time");
  const [cause, setCause] = useState("Where Most Needed");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const finalAmount = custom ? parseFloat(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email");
      return;
    }
    if (!finalAmount || finalAmount <= 0) {
      toast.error("Please select a donation amount");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/donations", {
        name: form.name,
        email: form.email,
        amount: finalAmount,
        frequency,
        cause,
        message: form.message,
      });
      setDone(data);
      toast.success("Donation recorded — thank you!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="donate-success" className="min-h-[80vh] bg-[#0A0A0C] flex items-center">
        <div className="max-w-3xl mx-auto px-6 text-center py-20">
          <Heart className="mx-auto text-[#E85D04]" size={64} fill="currentColor" />
          <h1 className="mt-8 font-serif-display text-5xl lg:text-6xl">Thank you, {done.name.split(" ")[0]}.</h1>
          <p className="mt-6 text-white/70 text-lg leading-relaxed">
            Your gift of <span className="text-[#E85D04] font-semibold">${done.amount}</span> {done.frequency === "monthly" ? "per month " : ""}
            is making lives possible. A confirmation has been sent to {done.email}.
          </p>
          <div className="mt-10 inline-block border border-white/10 px-8 py-6 text-left">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">Reference</div>
            <div className="font-serif-display text-3xl text-[#E85D04] mt-1">{done.reference}</div>
          </div>
          <p className="mt-8 text-xs text-white/40 italic">
            Note: Payment processing is currently mocked. No charge has been made.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="donate-page" className="bg-[#0A0A0C] text-white py-20">
      <section className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.3em] uppercase text-[#E85D04] mb-6">Give Today</div>
          <h1 className="font-serif-display text-5xl lg:text-7xl leading-[0.95] tracking-tight">
            Every dollar <em className="not-italic text-[#E85D04]">moves a life.</em>
          </h1>
          <div className="mt-10 space-y-6 text-white/70 leading-relaxed">
            <p><span className="text-white font-semibold">$25</span> — feeds a rescue dog for one month.</p>
            <p><span className="text-white font-semibold">$100</span> — vaccinates and sterilizes one animal.</p>
            <p><span className="text-white font-semibold">$500</span> — funds emergency rescue operations.</p>
            <p><span className="text-white font-semibold">$1,000</span> — supports a community aid program for 30 days.</p>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 border border-white/10 p-8 lg:p-12 bg-[#121215]">
          {/* Frequency */}
          <div className="flex border border-white/10">
            {[{ k: "one-time", l: "One-time" }, { k: "monthly", l: "Monthly" }].map((f) => (
              <button
                key={f.k}
                type="button"
                onClick={() => setFrequency(f.k)}
                data-testid={`freq-${f.k}`}
                className={`flex-1 py-4 text-xs tracking-[0.22em] uppercase transition-colors ${
                  frequency === f.k ? "bg-[#E85D04] text-white" : "text-white/60 hover:text-white"
                }`}
              >
                {f.l}
              </button>
            ))}
          </div>

          {/* Amount grid */}
          <div className="mt-8">
            <span className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">Amount</span>
            <div className="grid grid-cols-3 gap-3">
              {AMOUNTS.map((a) => (
                <button
                  key={a}
                  type="button"
                  data-testid={`donate-amount-${a}`}
                  onClick={() => { setAmount(a); setCustom(""); }}
                  className={`py-4 font-serif-display text-2xl border transition-colors ${
                    amount === a && !custom
                      ? "border-[#E85D04] text-[#E85D04] bg-[#E85D04]/10"
                      : "border-white/10 text-white hover:border-white/40"
                  }`}
                >
                  ${a}
                </button>
              ))}
            </div>
            <input
              type="number"
              data-testid="donate-custom"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Or enter custom amount"
              className="mt-3 w-full bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04] placeholder:text-white/30"
            />
          </div>

          {/* Cause */}
          <div className="mt-8">
            <span className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">Direct My Gift To</span>
            <select
              value={cause}
              onChange={(e) => setCause(e.target.value)}
              data-testid="donate-cause"
              className="w-full bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04]"
            >
              <option>Where Most Needed</option>
              <option>Stray Dog Rescue</option>
              <option>Wildlife Protection</option>
              <option>Community Aid</option>
              <option>Sanctuary Network</option>
            </select>
          </div>

          {/* Donor info */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <input data-testid="donate-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04] placeholder:text-white/30" />
            <input data-testid="donate-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04] placeholder:text-white/30" />
          </div>
          <textarea
            data-testid="donate-message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="Optional message"
            rows={3}
            className="mt-4 w-full bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04] placeholder:text-white/30 resize-none"
          />

          <button
            type="submit"
            disabled={submitting}
            data-testid="donate-submit"
            className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#E85D04] hover:bg-[#DC2F02] text-white px-8 py-5 text-xs tracking-[0.25em] uppercase disabled:opacity-60"
          >
            {submitting ? "Processing…" : `Donate $${finalAmount || 0} ${frequency === "monthly" ? "/ month" : ""}`}
            <ArrowRight size={14} />
          </button>
          <p className="mt-4 text-[11px] text-white/40 text-center italic">
            Demo donation. Payment processing is mocked for now.
          </p>
        </form>
      </section>
    </div>
  );
}
